import React, { FC, Fragment } from 'react';
import { Grid, Paper, Tab, Tabs, Typography } from '@material-ui/core';
import LinkIcon from '@material-ui/icons/Link';
import SubjectRoundedIcon from '@material-ui/icons/SubjectRounded';
import AddPhotoAlternateIcon from '@material-ui/icons/AddPhotoAlternate';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

import { PaddedPaper } from 'components';
import { useClient, useCurrentUser } from 'hooks';
import { isAdminUser } from 'helpers';

import {
  Commentary,
  Disclosures,
  ScheduleExpirePost,
  UrlTracking,
  FormButtons,
  GroupSelection,
  LinkForm,
  PinPost,
  TextForm,
  ImageVideoForm,
  MarkImportant,
} from '../components';
import { hasContributorPrivilege, hasModeratorPrivilege } from '../helpers';
import { usePostContext } from '../PostContext';
import { PostOption } from '../types';

const useStyles = makeStyles(theme => ({
  container: {
    borderTop: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  tabs: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  tabRoot: {
    borderRight: `1px solid ${theme.palette.grey[200]}`,
  },
  tabWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    justify: 'space-between',
    padding: theme.spacing(1, 0),
  },
  tabIcon: {
    marginRight: theme.spacing(1),
  },
}));

const Post: FC = ({ children }) => {
  const classes = useStyles();
  const currentUser = useCurrentUser();
  const { t } = useTranslation();
  const { moderator_can_mark_as_important } = useClient();
  const { state, dispatch } = usePostContext();

  const hasContributorWidgets = hasContributorPrivilege(currentUser, state.moderatorMode);
  const hasModeratorWidgets = hasModeratorPrivilege(currentUser, state.moderatorMode);
  const hasMarkImportant =
    (hasModeratorWidgets && moderator_can_mark_as_important) || isAdminUser(currentUser);
  const isLinkForm = state.option === PostOption.Link;

  function handleChangeTab(event: unknown, value: PostOption) {
    dispatch({ type: 'option', value });
  }

  function renderTabs() {
    return (
      <Tabs
        value={state.option}
        onChange={handleChangeTab}
        textColor="primary"
        indicatorColor="primary"
        variant="fullWidth">
        <Tab
          value={PostOption.Link}
          classes={{ wrapper: classes.tabWrapper, root: classes.tabRoot }}
          label={
            <Fragment>
              <LinkIcon className={classes.tabIcon} />
              {t('common:link')}
            </Fragment>
          }
        />
        <Tab
          value={PostOption.ImageVideo}
          classes={{ wrapper: classes.tabWrapper, root: classes.tabRoot }}
          label={
            <Fragment>
              <AddPhotoAlternateIcon className={classes.tabIcon} />
              {t('posting:imageOrVideo')}
            </Fragment>
          }
        />
        <Tab
          value={PostOption.Text}
          classes={{ wrapper: classes.tabWrapper }}
          label={
            <Fragment>
              <SubjectRoundedIcon className={classes.tabIcon} />
              {t('common:Text')}
            </Fragment>
          }
        />
      </Tabs>
    );
  }

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <Typography variant="h4">{t('posting:createPost')}</Typography>
      </Grid>

      <GroupSelection />

      <Grid item>
        <Typography variant="subtitle1">{t('posting:createPostSubhead')}</Typography>
      </Grid>

      <Grid item container direction="column" wrap="nowrap">
        <Grid item xs>
          <Paper variant="outlined" className={classes.tabs}>
            {renderTabs()}
          </Paper>
        </Grid>
        <Grid item xs>
          <PaddedPaper className={classes.container}>
            {state.option === PostOption.Link && <LinkForm />}
            {state.option === PostOption.ImageVideo && <ImageVideoForm />}
            {state.option === PostOption.Text && <TextForm />}
          </PaddedPaper>
        </Grid>
      </Grid>

      {hasMarkImportant && (
        <Grid item container>
          <MarkImportant />
        </Grid>
      )}

      {hasModeratorWidgets && (
        <Grid item container>
          <PinPost />
        </Grid>
      )}

      {hasModeratorWidgets && (
        <Grid item container>
          <ScheduleExpirePost />
        </Grid>
      )}

      {hasContributorWidgets && (
        <Grid item container>
          <Commentary />
        </Grid>
      )}

      {hasModeratorWidgets && (
        <Grid item container>
          <Disclosures />
        </Grid>
      )}

      {isLinkForm && hasModeratorWidgets && <UrlTracking />}

      {children}

      <FormButtons />
    </Grid>
  );
};

export default Post;
